import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import * as landlordQueries from '~settings/queries';
import { fetch } from '~helpers/graphql';
import PropertySelectorModal from '~components/property-selector/modal';
import { formatSimpleParams } from '~helpers/params';
import { propertyListingForm } from '~client/constants';

export default class PropertySelector extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      properties: props.properties,
      loading: !props.properties,
      showModal: false,
      modalIsOnTop: false,
    };
  }

  // this function will return a promise to tell what user does
  openToSelect = () => {
    if (!this.state.showModal) {
      this.toogleModal();
    }
    return new Promise(resolve => {
      this.selectCallback = properties => {
        if (properties) {
          resolve({ select: true, properties });
        } else {
          resolve({ select: false, properties: null });
        }
        this.selectCallback = null;
      };
    });
  };

  groupPropertiesByCountry = properties =>
    properties
      .map(item => item.node)
      .reduce((groupedProperties, property) => {
        const { country } = property.city;
        if (groupedProperties[country.name]) {
          groupedProperties[country.name].push(property);
        } else {
          // eslint-disable-next-line
          groupedProperties[country.name] = [property];
        }
        return groupedProperties;
      }, {});

  componentDidMount() {
    const { properties, propertiesType } = this.props;
    if (!properties) {
      fetch({
        query: landlordQueries.getLandLordSimpleProperties(
          formatSimpleParams(
            {
              bookingJourneys: null,
            },
            {
              bookingJourneys: propertiesType,
            },
          ),
        ),
      })
        .then(res => {
          this.setState({
            loading: false,
            properties: this.groupPropertiesByCountry(res.viewer.properties.edges),
          });
        })
        .catch(e => {
          console.error('Error in getLandLordSimpleProperties:', e);
          this.setState({
            loading: false,
            properties: [],
          });
        });
    }
  }

  toogleModal = () => {
    if (!this.state.showModal) {
      const { top } = this.listElement.getBoundingClientRect();
      this.setState({
        showModal: true,
        modalIsOnTop: top > 300,
      });
    } else {
      this.setState({
        showModal: false,
      });
    }
  };

  fetchAppliedResult = properties => {
    if (properties?.length === 0) return;
    const queryParams = properties.map(item => ({ propertyId: item.id }));
    const variables = { properties: queryParams };
    fetch({ query: landlordQueries.checkPropertyOfWithdrawMethod(variables) })
      .then(res => {
        if (res?.checkPropertyOfWithdrawMethod?.result) {
          if (this.selectCallback) {
            this.selectCallback(properties);
          }
          this.props.onChange(properties);
        }
      })
      .catch(e => {
        if (e.message === 'INVALID_PROPERTY_AP_CATEGORY') {
          this.handleShowAppliedErrorModal();
        }
      });
  };

  handleShowAppliedErrorModal = () => {
    Modal.error({
      title: this.props.t('landlord.payment.list.column.applied_properties'),
      content: this.props.t('landlord.withdrawal.applied_properties.error_modal.content'),
      okText: this.props.t('landlord.booking.reconciliation.details.stage.modal.confirm'),
      bodyStyle: { padding: '24px' },
    });
  };

  handleSelectedChange = properties => {
    this.setState({
      showModal: false,
    });

    if (this.props.needValidatePayment) {
      this.fetchAppliedResult(properties);
    } else {
      if (this.selectCallback && this.props.needValidatePayment) {
        this.selectCallback(properties);
      }
      this.props.onChange(properties);
    }
  };

  deleteProperty = slug => {
    const selectProperties = this.props.propertiesSelected.filter(
      property => slug !== property.slug,
    );

    this.props.onChange(selectProperties);
  };

  closeModal = () => {
    if (this.selectCallback) {
      this.selectCallback(null);
    }
    this.toogleModal();
  };

  render() {
    const {
      propertiesSlugsExcluded,
      propertiesSelected,
      t,
      className,
      onlyOneCountry,
      isDisabledsetProperty,
    } = this.props;

    return (
      <div className={`property-selector ${className}`}>
        <div
          className="property-selector__list"
          ref={list => {
            this.listElement = list;
          }}
        >
          <div
            className="property-selector__add"
            disabled={isDisabledsetProperty}
            onClick={this.toogleModal}
            role="presentation"
          >
            <PlusOutlined className="property-selector__add-icon" />
            <span>{t('landlord.global.btn.select_property')}</span>
          </div>
          <For each="selectedItem" of={propertiesSelected} index="idx">
            <If condition={selectedItem?.status !== propertyListingForm.statuses.published}>
              <div className="property-selector__item" key={selectedItem.slug}>
                <span>{selectedItem.name}</span>

                <CloseOutlined
                  onClick={() => {
                    this.deleteProperty(selectedItem.slug);
                  }}
                  className="property-selector__item-icon"
                />
              </div>
            </If>
            <If condition={selectedItem?.status === propertyListingForm.statuses.published}>
              <Tooltip placement="top" title={t('payment_plan_basic_info_property_tooltip')}>
                <div className="property-selector__item" key={selectedItem.slug}>
                  <span>{selectedItem.name}</span>
                </div>
              </Tooltip>
            </If>
          </For>
          <If condition={this.state.showModal}>
            <div
              className="property-selector__overlay"
              onClick={this.closeModal}
              role="presentation"
            />
            <PropertySelectorModal
              t={t}
              properties={this.state.properties}
              propertiesSelected={propertiesSelected}
              propertiesSlugsExcluded={propertiesSlugsExcluded}
              loading={this.state.loading}
              isOnTop={this.state.modalIsOnTop}
              onSubmit={this.handleSelectedChange}
              onlyOneCountry={onlyOneCountry}
            />
          </If>
        </div>
        <If condition={isDisabledsetProperty}>
          <div className="property-selector__noPayment">
            <span>{t('landlord.detail.no_payment_access.not_allowed.diabled.hint')}</span>
          </div>
        </If>
      </div>
    );
  }
}

PropertySelector.propTypes = {
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
    }),
  ),
  propertiesType: PropTypes.array,
  propertiesSelected: PropTypes.array.isRequired,
  propertiesSlugsExcluded: PropTypes.array,
  t: PropTypes.func,
  onChange: PropTypes.func,
  className: PropTypes.string,
  onlyOneCountry: PropTypes.bool,
  needValidatePayment: PropTypes.bool,
};

PropertySelector.defaultProps = {
  t: () => {},
  properties: null,
  propertiesType: [],
  propertiesSelected: [],
  propertiesSlugsExcluded: [],
  onChange: () => {},
  className: '',
  onlyOneCountry: true,
  needValidatePayment: false,
};
